import "./ContactDetails.css";
import { useCallback, useRef, useState } from "react";
import classNames from "classnames";
import {
  Booking,
  Contact,
  ToolCall,
  TranslatedString,
  Assignation,
} from "../interfaces";
import { FormattedMessage, useIntl } from "react-intl";
import { useMutation } from "@tanstack/react-query";
import { useIonToast } from "@ionic/react";
import {
  IconAlertSquareRounded,
  IconSquareRoundedCheck,
  IconArchive,
  IconForbid2,
  IconUserFilled,
} from "@tabler/icons-react";

import { parseDate } from "../utils/moment";
import ContactsApi from "../api/contacts";
import AssignationApi from "../api/assignation";

interface LayoutProps {
  bookings: Booking[];
  contact: Contact;
  toolCalls: ToolCall[];
  createEscalation: Function;
  solveEscalations: Function;
  toggleBlock: Function;
}

type StringOrObject =
  | string
  | { [key: string]: StringOrObject }
  | StringOrObject[];

interface ArgumentProps {
  args: StringOrObject;
  k?: string;
}

const Argument: React.FC<ArgumentProps> = (data) => {
  const { args, k } = data;

  if (
    typeof args === "string" ||
    typeof args === "boolean" ||
    typeof args === "number"
  ) {
    return (
      <span className="badge auto-capitalize text-bg-light mb-1 mt-1 mr-1">
        {String(args)}
      </span>
    );
  }

  if (Array.isArray(args)) {
    return (
      <>
        {args.map((item, index) => (
          <Argument args={item} key={`${k}-${index}`} k={`${k}-${index}`} />
        ))}
      </>
    );
  }

  if (typeof args === "object" && args !== null) {
    return (
      <div
        className={k === undefined ? "d-flex overflow-auto pb-1" : "d-flex "}
      >
        {Object.entries(args).map(([k, v]) => (
          <div key={k} className="flex align-items-center">
            <span className="badge auto-capitalize text-bg-light mr-1">
              {k}
            </span>
            <Argument args={v} k={k}></Argument>
          </div>
        ))}
      </div>
    );
  }

  return <></>;
};

const ContactDetails: React.FC<LayoutProps> = (props) => {
  const intl = useIntl();
  const currentLanguage = intl.locale as keyof TranslatedString;
  const {
    bookings,
    contact,
    createEscalation,
    solveEscalations,
    toggleBlock,
    toolCalls,
  } = props;

  const [companyUsers, setCompanyUsers] = useState<Assignation[]>([]);
  const [loading, setLoading] = useState(false);
  const loadedForContactRef = useRef<string | null>(null);

  const intent = contact.intent?.[currentLanguage];
  const intentDescription = contact.intentDescription?.[currentLanguage];

  const [present] = useIonToast();

  const mutateArchive = useMutation({
    mutationFn: ContactsApi.putArchive,
    onSuccess: (data) => {
      if (!data.success) {
        return present({
          message: intl.formatMessage({ id: "common.tryItLater" }),
          duration: 6000,
          color: "light",
          position: "top",
          cssClass: "error-toast",
        });
      }
    },
    onError: () => {
      return present({
        message: intl.formatMessage({ id: "common.tryItLater" }),
        duration: 6000,
        color: "light",
        position: "top",
        cssClass: "error-toast",
      });
    },
  });

  const fetchCompanyUsers = useCallback(async () => {
    if (loadedForContactRef.current === contact.id) return;
    setLoading(true);
    try {
      const { data } = await AssignationApi.getAssignableUsers(contact.id);
      if (data) {
        setCompanyUsers(data.users);
      }
      loadedForContactRef.current = contact.id;
    } catch (e) {
      console.log(e);
    } finally {
      setLoading(false);
    }
  }, [contact.id]);

  const onChangeAssignation = async (newUserId: string) => {
    try {
      if (newUserId === "unassigned") {
        return await AssignationApi.unassignUserToContact({
          contactId: contact.id,
          userId: contact.assignations[0]?.id || "",
        });
      }
      await AssignationApi.assignUserToContact({
        contactId: contact.id,
        userId: newUserId,
      });
    } catch (e) {
      console.log(e);
    } finally {
      setLoading(false);
    }
  };

  const scrollToMessage = (messageId: string) => {
    const messageEl = document.getElementById(messageId);
    if (messageEl) {
      return messageEl.scrollIntoView({ block: "center", behavior: "smooth" });
    }
  };

  const toolCallsDiv = (
    <>
      <h5 className="fs-4 color-step-900">
        <FormattedMessage id="contactDetail.toolCalls" />
      </h5>
      <div className="contact-details-bookings flex flex-column gap-3">
        {toolCalls.map((call) => (
          <div
            key={call.id}
            className="flex flex-column gap-2"
            onClick={() => scrollToMessage(String(call.message))}
          >
            <div className="font-14 color-step-800">
              <div>
                {" "}
                <FormattedMessage id={`toolCalls.${call.name}`} />
              </div>
              <div className="font-12 color-step-500">{call.createdAt}</div>
            </div>
          </div>
        ))}
      </div>
    </>
  );

  const bookingsDiv = (
    <>
      <h5 className="fs-4 color-step-900">
        <FormattedMessage id="contactDetail.bookings" />
      </h5>
      <div className="contact-details-bookings">
        {bookings.map((b, key) => (
          <div style={{ paddingBottom: "15px" }} key={key}>
            <div className="booking-id font-14 color-step-800">
              <div>{b.externalId}</div>
              <div>
                <span
                  className={classNames("badge auto-capitalize", {
                    "text-bg-danger": b.status === "cancelled",
                    "text-bg-success": b.status === "confirmed",
                    "text-bg-light": ["checked_out", "checked_in"].includes(
                      b.status
                    ),
                    "text-bg-dark": ![
                      "cancelled",
                      "confirmed",
                      "checked_out",
                      "checked_in",
                    ].includes(b.status),
                  })}
                >
                  {b.status.replace("_", " ")}
                </span>
              </div>
            </div>
            <div className="font-14 color-step-800">{b.locationName}</div>
            {b.rooms?.map((r, k) => (
              <div key={k} className="font-14 color-step-800">
                {r.name}
              </div>
            ))}
            <div className="font-12 color-step-500">
              {parseDate(b.from, "ddd Do, MMM YYYY")}
              <br />
              {parseDate(b.to, "ddd Do, MMM YYYY")}
            </div>
          </div>
        ))}
      </div>
    </>
  );

  return contact.id ? (
    <div className="contact-details-container">
      <h5 className="fs-4 color-step-900">
        <FormattedMessage id="contactDetail.title" />{" "}
      </h5>
      <div className="contact-details-profile">
        <div className="color-step-800 font-14">{contact.name}</div>
        {contact.username && (
          <div className="color-step-800 font-14">@{contact.username}</div>
        )}
        <div className="color-step-500 font-14">
          <FormattedMessage id={`integrations.${contact.type}Name`} />
        </div>
        {["wa", "voice"].includes(contact.type) ? (
          <div className="color-step-500 font-14">{contact.phoneNumber}</div>
        ) : null}
        {contact.followers ? (
          <div className="color-step-500 font-14">
            <b>{contact.followers}</b>{" "}
            <FormattedMessage id="contactDetail.followers" />
          </div>
        ) : null}
        {contact.igFmPageSource && (
          <div className="color-step-800 font-14 mt-1">
            <span>Via: {contact.igFmPageSource}</span>
          </div>
        )}
      </div>

      <div className="contact-details-profile">
        <div className="flex flex-column gap-1 align-items-start">
          <button
            onClick={() =>
              contact.escalated
                ? solveEscalations(contact.id)
                : createEscalation(contact.id)
            }
            className={`btn ${
              contact.escalated ? "btn-visito-link" : "btn-visito-link-warning"
            } btn-xs lh-normal flex align-items-center gap-1`}
          >
            {contact.escalated ? (
              <IconSquareRoundedCheck size={19} />
            ) : (
              <IconAlertSquareRounded size={19} />
            )}
            <FormattedMessage
              id={contact.escalated ? "chats.markAsClose" : "chats.markAsOpen"}
            />
          </button>

          <button
            onClick={() =>
              mutateArchive.mutate({
                contactId: contact.id,
                archive: !contact.archive,
              })
            }
            className="btn btn-xs btn-visito-link-neutral lh-normal flex align-items-center gap-1"
          >
            <IconArchive size={19} />
            <FormattedMessage
              id={contact.archive ? `chats.unArchive` : `chats.archive`}
            />
          </button>

          <button
            onClick={() => toggleBlock(contact)}
            className="btn btn-visito-link-danger btn-xs lh-normal flex align-items-center gap-1"
          >
            <IconForbid2 size={19} />
            <FormattedMessage
              id={
                contact.blocked ? `chats.unblockContact` : `chats.blockContact`
              }
            />
          </button>
        </div>
      </div>
      <div className="contact-details-profile">
        <h5 className="fs-4 color-step-900">
          <FormattedMessage id="contactDetail.assignation" />
        </h5>
        <select
          className="form-select form-select-sm"
          style={{ maxWidth: 320 }}
          value={contact.assignations[0]?.id ?? ""}
          onMouseDown={fetchCompanyUsers}
          onFocus={fetchCompanyUsers}
          onChange={(e) => onChangeAssignation(e.target.value)}
        >
          <option value="" disabled>
            <FormattedMessage id="contactDetail.assignUser" />
          </option>

          {Array.from(
            new Map(
              contact.assignations.concat(companyUsers).map((u) => [u.id, u]) // last one wins if duplicates
            ).values()
          ).map((u) => (
            <option key={u.id} value={u.id}>
              {u.email}
            </option>
          ))}
          {loading ? (
            <option value="" disabled>
              <>
                <FormattedMessage id="common.loading" />
                ...
              </>
            </option>
          ) : null}
          <option value="unassigned">
            <FormattedMessage id="contactDetail.unassignUser" />
          </option>
        </select>
      </div>
      {contact.optedOut ? (
        <div className="contact-details-profile">
          <FormattedMessage id="chats.contactOptedOut" />
        </div>
      ) : null}
      {bookings.length ? bookingsDiv : null}
      {intent ? (
        <div>
          <div className="contact-details-bookings">
            <h5 className="fs-4 color-step-900">
              <FormattedMessage id="leads.intent" />
            </h5>
            <div className="color-step-900 fs-3">{intent}</div>
            <div>
              <span className="fs-2 color-step-700">{intentDescription}</span>
            </div>
          </div>
        </div>
      ) : null}

      {toolCalls.length ? toolCallsDiv : null}
    </div>
  ) : null;
};

export default ContactDetails;
